import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { bookingsAPI, venuesAPI } from '../services/api';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  RefreshCw,
  CalendarDays
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const YearView = () => {
  const navigate = useNavigate();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [bookings, setBookings] = useState([]);
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const loadBookings = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await bookingsAPI.getAll();
      setBookings(data || []);
    } catch (error) {
      console.error('Failed to load bookings:', error);
      toast({ title: 'Error', description: 'Failed to load bookings', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const loadVenues = useCallback(async () => {
    try {
      const data = await venuesAPI.getAll();
      setVenues(data || []);
    } catch (error) {
      console.error('Failed to load venues:', error);
    }
  }, []);

  useEffect(() => {
    loadBookings();
    loadVenues();
  }, [loadBookings, loadVenues]);

  const filteredBookings = selectedVenue === 'all' 
    ? bookings 
    : bookings.filter(b => b.venueId === selectedVenue || b.venueName === selectedVenue);

  const getDateBookings = (year, month, day) => {
    const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    const checkDate = new Date(year, month, day);
    checkDate.setHours(0, 0, 0, 0);
    
    return filteredBookings.filter(booking => {
      const startDateStr = booking.eventDate?.split('T')[0];
      const endDateStr = booking.eventEndDate?.split('T')[0];
      if (!startDateStr) return false;
      if (!endDateStr || endDateStr === startDateStr || endDateStr === 'null' || endDateStr === 'None') {
        return startDateStr === dateStr;
      }
      const bookingStart = new Date(startDateStr);
      bookingStart.setHours(0, 0, 0, 0);
      const bookingEnd = new Date(endDateStr);
      bookingEnd.setHours(23, 59, 59, 999);
      return checkDate >= bookingStart && checkDate <= bookingEnd;
    });
  };

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const isToday = (year, month, day) => {
    const today = new Date();
    return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
  };

  const handleDateClick = (year, month, day) => {
    navigate(`/calendar?date=${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`);
  };

  const getDateInfo = (year, month, day) => {
    const today = isToday(year, month, day);
    const dateBookings = getDateBookings(year, month, day);
    
    if (today) {
      return { bgColor: 'bg-teal-500', textColor: 'text-white font-bold', isBooked: false, isToday: true };
    }
    
    if (dateBookings.length > 0) {
      const hasBlocked = dateBookings.some(b => 
        b.bookingType?.toLowerCase() === 'blocked' || b.status?.toLowerCase() === 'blocked'
      );
      if (hasBlocked) {
        return { bgColor: 'bg-yellow-400', textColor: 'text-yellow-900 font-semibold', isBooked: true, isToday: false };
      }
      return { bgColor: 'bg-red-500 dark:bg-amber-600', textColor: 'text-white font-semibold', isBooked: true, isToday: false };
    }
    
    return { bgColor: '', textColor: 'text-gray-600 dark:text-gray-400', isBooked: false, isToday: false };
  };

  const renderMonth = (monthIndex) => {
    const daysInMonth = getDaysInMonth(currentYear, monthIndex);
    const firstDay = getFirstDayOfMonth(currentYear, monthIndex);
    const weeks = [];
    let days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<td key={`empty-start-${i}`} className="p-0"><div className="w-4 h-4"></div></td>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateInfo = getDateInfo(currentYear, monthIndex, day);
      days.push(
        <td key={day} className="p-0 text-center" onClick={() => handleDateClick(currentYear, monthIndex, day)}>
          <div className={`
            w-4 h-4 mx-auto flex items-center justify-center 
            text-[8px] cursor-pointer rounded-sm transition-all
            ${dateInfo.isToday ? dateInfo.bgColor + ' ' + dateInfo.textColor : ''}
            ${dateInfo.isBooked && !dateInfo.isToday ? dateInfo.bgColor + ' ' + dateInfo.textColor : ''}
            ${!dateInfo.isBooked && !dateInfo.isToday ? dateInfo.textColor + ' hover:bg-gray-100 dark:hover:bg-gray-700' : ''}
          `}>
            {day}
          </div>
        </td>
      );
      
      if (days.length === 7) {
        weeks.push(<tr key={`week-${weeks.length}`}>{days}</tr>);
        days = [];
      }
    }
    
    if (days.length > 0) {
      const remainingCells = 7 - days.length;
      for (let i = 0; i < remainingCells; i++) {
        days.push(<td key={`empty-end-${i}`} className="p-0"><div className="w-4 h-4"></div></td>);
      }
      weeks.push(<tr key={`week-${weeks.length}`}>{days}</tr>);
    }
    
    return weeks;
  };

  return (
    <div className="space-y-4" data-testid="year-view-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-1 sm:gap-3 mb-1">
            <a href="/calendar" className="text-lg sm:text-2xl font-bold text-gray-400 dark:text-muted-foreground flex items-center gap-1.5 hover:text-teal-600 dark:hover:text-primary transition-colors pb-1" data-testid="nav-calendar">
              <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
              CALENDAR
            </a>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-lg sm:text-2xl font-bold text-teal-600 dark:text-primary flex items-center gap-1.5 border-b-2 border-teal-600 pb-1" data-testid="nav-year-view-active">
              <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
              YEAR VIEW
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={selectedVenue} onValueChange={setSelectedVenue}>
            <SelectTrigger className="w-[140px] sm:w-[180px] h-8 text-xs sm:text-sm" data-testid="venue-filter">
              <SelectValue placeholder="All Venues" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Venues</SelectItem>
              {venues.map(venue => (
                <SelectItem key={venue.id} value={venue.id}>{venue.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={() => { loadBookings(); loadVenues(); }}
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={isLoading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Year Navigation */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => setCurrentYear(prev => prev - 1)} className="h-8 w-8" data-testid="prev-year">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-foreground" data-testid="current-year">
          {currentYear}
        </h2>
        <Button variant="ghost" size="icon" onClick={() => setCurrentYear(prev => prev + 1)} className="h-8 w-8" data-testid="next-year">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* 12 Months Grid */}
      <div className="grid grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {MONTHS.map((month, index) => {
          const isCurrentMonth = new Date().getFullYear() === currentYear && new Date().getMonth() === index;
          return (
            <div key={month} className="text-center" data-testid={`month-${month.toLowerCase()}`}>
              <h3 className={`font-semibold text-sm sm:text-base mb-2 ${
                isCurrentMonth ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
              }`}>
                {month}
              </h3>
              <table className="w-full table-fixed">
                <tbody>
                  {renderMonth(index)}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs text-gray-600 dark:text-gray-400 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-3 sm:w-6 sm:h-3 bg-red-500 dark:bg-amber-600 rounded flex-shrink-0"></div>
          <span>Confirmed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-3 sm:w-6 sm:h-3 bg-yellow-400 rounded flex-shrink-0"></div>
          <span>Blocked</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-3 sm:w-6 sm:h-3 bg-teal-500 rounded flex-shrink-0"></div>
          <span>Today</span>
        </div>
      </div>
    </div>
  );
};

export default YearView;
